import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import datefns from 'date-fns';
import MonthHeader from './MonthHeader';
import TrowFragment from './TrowFragment';
import Popup from './Popup';
import PopupContent from './PopupContent';
import CloseButton from './CloseButton';

/**
 * DateMenu is a Menu that can be "open" or not.
 * When closed it appears to be a button. When open, it
 * utilizes a portal to display a div beside it with
 * clickable date-labeled buttons.
 *
 * @reactProps {string[]} dates - a list of date strings that will become buttons in the open div
 * @reactProps {function} handleMenuToggle - from MenuBox (parent), handles the style logic
 * @reactProps {function} handleChange - from Main, handleDateChange
 * @reactProps {boolean} open - the source of style logic
 * @reactProps {string} title - the label for the closed menu
 */
export default class DateMenu extends React.Component {
  state = {};

  /**
   * dates must be sorted, bounding rectangle for portal established
   */
  componentDidMount() {
    this.parseDates(this.props.dates);
    this.getStyle();
    window.addEventListener('resize', () => this.getStyle());
    document.addEventListener('scroll', () => this.getStyle());
  }

  /**
   * Set the rectangle object to state. The open menu div
   * has to be positioned relative to this rectangle.
   */
  getStyle = () => {
    const parent = document.getElementById('date-menu-button');
    const rect = parent.getBoundingClientRect();
    this.setState({ parent, rect });
  };

  /**
   * dates are parsed as datefns objects and sorted latest first
   */
  parseDates(arrOfDates) {
    const sortedDates = arrOfDates
      .map(d => datefns.parse(d))
      .sort((date1, date2) => date2 - date1);
    //.map( d => (datefns.format(d, "YYYYMMDD")));

    this.setState({ sortedDates });
  }

  /**
   * When closed, renders a button displaying props.title
   *  Style determined with props.open, deconstructed and
   *  controlling the "open" className via cx.
   * When open, renders the button displaying props.title
   *  and a Popup > div > PopupContent.
   *  PopupContent children include CloseButton, MonthHeader,
   *   and TrowFragment
   * @return button, Popup > div > PopupContent
   */
  render() {
    const { open } = this.props;
    if (!this.state) {
      return (
        <button
          id={'date-menu-button'}
          className={cx('closed-menu', 'date-menu', { open: open })}
          onClick={this.props.handleMenuToggle}
        >
          <h3>{this.props.title}</h3>
        </button>
      );
    } else {
      return (
        <button
          id={'date-menu-button'}
          className={cx('closed-menu', 'date-menu', { open: open })}
          onClick={this.props.handleMenuToggle}
        >
          <h3>{this.props.title}</h3>
          {open && (
            <Popup>
              <div className="date-menu opened">
                <PopupContent
                  top={this.state.rect.top}
                  left={this.state.rect.right + 5}
                >
                  <CloseButton
                    handleCloseButton={this.props.handleCloseButton}
                  />
                  {this.state.sortedDates.map((d, i, arr) => {
                    let date = datefns.format(d, 'YYYYMMDD');
                    if (
                      i === 0 ||
                      datefns.getYear(d) !== datefns.getYear(arr[i - 1])
                    ) {
                      return (
                        <div>
                          <div className="category">{datefns.getYear(d)}</div>
                          <MonthHeader month={datefns.getMonth(d)} />
                          <TrowFragment
                            onEvent={this.props.handleChange}
                            date={date}
                          />
                        </div>
                      );
                    } else {
                      if (
                        datefns.getMonth(d) !== datefns.getMonth(arr[i - 1])
                      ) {
                        return (
                          <div>
                            <MonthHeader month={datefns.getMonth(d)} />
                            <TrowFragment
                              onEvent={this.props.handleChange}
                              date={date}
                            />
                          </div>
                        );
                      }

                      return (
                        <TrowFragment
                          onEvent={this.props.handleChange}
                          date={date}
                        />
                      );
                    }
                  })}
                </PopupContent>
              </div>
            </Popup>
          )}
        </button>
      );
    }
  }
}

DateMenu.propTypes = {
  dates: PropTypes.array,
  handleChange: PropTypes.func,
  handleMenuToggle: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.string
};



// WEBPACK FOOTER //
// ./src/DateMenu.js